import { FC, useState, useEffect } from "react";
import cx from "classnames";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import { MobileModal } from "@/components/Modal";
import { Skeleton } from "components/Loader";
import {
  useCourseQuery,
  Step,
  StepType,
  useCaptureActivityMutation,
} from "@/fetch/academy";
import { useTrackers, useCurrentTrip, useFeatureFlags } from "@/hooks";
import Tabs, { Tab } from "@/components/Tabs";
import Typography from "@/components/Typography";
import { CloseIcon } from "@/components/Icon";
import DotSeparator from "@/components/DotSeparator";
import Tag from "@/components/Tag";
import Accordion, {
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import {
  ChevronIcon,
  PlayCircleIcon,
  FileIcon,
  TickIcon,
  FeedIcon,
} from "@/components/Icon";
import Button from "@/components/Button";
import CategoryCoursesModal from "./CategoryCoursesModal";
import LockedDialog from "./LockedDialog";
import VideoPreview from "./VideoPreview";
import styles from "./MobileCoursePage.module.scss";

const MobileCoursePage: FC = () => {
  const [tab, setTab] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<Step | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined
  );
  const { query, back } = useRouter();
  const { courseId } = query;
  const { data: courseData, isLoading } = useCourseQuery(courseId as string);
  const { track } = useTrackers();
  const { currentTrip } = useCurrentTrip();
  const currentCourse = courseData?.response?.[0];

  const {
    flags: { gworldAcademy },
  } = useFeatureFlags();

  const { mutateAsync: captureActivity } = useCaptureActivityMutation(
    currentCourse?.id || 0
  );

  useEffect(() => {
    const currentCourse = courseData?.response?.[0];
    if (!currentCourse) return;
    track("Course Opened", {
      ...currentCourse,
      courseId: currentCourse?.id || null,
      cid: currentTrip?.cid || null,
      eventId: "course-opened",
    });
  }, [courseData]);

  const allSteps = currentCourse?.sections.length
    ? currentCourse?.sections
        .map((each) => each.steps.length)
        .reduce((prev, curr) => prev + curr)
    : 0;

  const duration = dayjs
    .duration(currentCourse?.total_length_in_min || 0, "minutes")
    .format("H[h] m[m]")
    .replace(/\b0+[a-z]+\s*/gi, "")
    .trim();

  const onGoBack = () => {
    back();
  };

  const timestamp = Math.floor(new Date().getTime() / 1000);
  const bannerImage = `https://res.cloudinary.com/gwatco/image/upload/f_auto,q_auto,h_180,dpr_2.0/v${timestamp}/${currentCourse?.cover}.png`;

  const onCaptureActivity = async (stepId: number, progress: number) => {
    const data = { stepId, progress };
    await captureActivity(data);
    if (progress > 0.9) {
      setCurrentStep({ ...(currentStep as Step), step_is_seen: 1 });
    }
  };

  const isFirstStep = (stepId: number) => {
    const allOtherSteps =
      currentCourse?.sections
        .map((each) => each.steps)
        .flat()
        .filter((each) => each.id !== currentStep?.id) || [];
    const seenStep = allOtherSteps.find((eachStep) => !!eachStep.step_is_seen);
    return !Boolean(seenStep);
  };

  const checkCourseCompleted = () => {
    const allOtherSteps =
      currentCourse?.sections
        .map((each) => each.steps)
        .flat()
        .filter((each) => each.id !== currentStep?.id) || [];
    const otherUnseenStep = allOtherSteps.find((each) => !each.step_is_seen);
    if (!Boolean(otherUnseenStep)) {
      track("Course Completed", {
        cid: currentTrip?.cid || null,
        course: currentCourse,
        eventId: "course-completed",
        description: "All steps are completed.",
      });
    }
  };

  useEffect(() => {
    if (!currentStep?.id) return;
    if (currentStep.type === StepType.Text) {
      if (currentStep.step_is_seen) return;
      if (isFirstStep(currentStep.id)) {
        track("Course Enrolled", {
          courseId: currentCourse?.id,
          courseName: currentCourse?.title,
          eventId: "course-enrolled",
        });
      }
      checkCourseCompleted();
      onCaptureActivity(currentStep.id, 1);
    }
  }, [currentStep]);

  const onProgress = (progress: number) => {
    if (!currentStep?.id) return;
    if (currentStep?.step_is_seen) return;
    if (progress < 0.1) return;
    onCaptureActivity(currentStep.id || 0, progress);
    if (isFirstStep(currentStep.id)) {
      track("Course Enrolled", {
        courseId: currentCourse?.id,
        courseName: currentCourse?.title,
        eventId: "course-enrolled",
        progress: `${progress * 100}%`,
      });
    }
    if (progress === 0.9) {
      track("Video Fully Watched", {
        cid: currentTrip?.cid || null,
        course: currentCourse,
        step: currentStep,
        eventId: "video-fully-watched",
        description: "Video passed 90% progress",
      });
      checkCourseCompleted();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const courseTabs = [
    {
      label: "Overview",
      value: 0,
    },
    {
      label: "Contents",
      value: 1,
    },
  ];
  const totalInMinutes = currentCourse?.total_length_in_min || 1;
  const totalTime =
    Math.floor(totalInMinutes / 60) > 0
      ? `${Math.floor(totalInMinutes / 60)}h ${totalInMinutes % 60}m`
      : `${totalInMinutes % 60}m`;

  const sections = [...(currentCourse?.sections || [])].sort(
    (firstSec, secondSec) => firstSec.order - secondSec.order
  );

  const getStepDuration = (minutes: number) => {
    return dayjs
      .duration(minutes, "minutes")
      .format(minutes > 59 ? "HH:mm:ss" : "mm:ss");
  };

  const isCourseStarted = Boolean(
    currentCourse?.sections
      .map((each) => each.steps)
      .flat()
      .find((each) => each.step_is_seen)
  );

  const goToFirstUnseenStep = () => {
    console.log("click");
    const sortedSections = [...sections].sort(
      (firstSection, secondSection) => firstSection.order - secondSection.order
    );
    const sortedSteps = sortedSections
      .map((eachSection) => {
        const sortedSteps = [...eachSection.steps].sort(
          (firstStep, secondStep) => firstStep.order - secondStep.order
        );
        return sortedSteps;
      })
      .flat();
    const firstUnseenStep = sortedSteps.find(
      (eachStep) => !eachStep.step_is_seen
    );
    setCurrentStep(firstUnseenStep);
    setTab(1);
  };

  return (
    <>
      <MobileModal>
        <div className={cx(styles.root, { [styles.scrollRoot]: tab !== 1 })}>
          <CloseIcon onClick={onGoBack} className={styles.closeIcon} />
          {isLoading ? (
            <Skeleton variant="rectangular" className={styles.mediaLoading} />
          ) : (
            <div className={styles.mediaContainer}>
              {currentStep ? (
                <>
                  {currentStep.type === StepType.Video && (
                    <div className={styles.videoContainer}>
                      <VideoPreview
                        key={currentStep.id}
                        stepId={currentStep.id}
                        onProgressUpdate={onProgress}
                        trackerData={{
                          content_asset_id: `academy_${currentCourse?.id}_${currentStep.id}`,
                          courseTitle: currentCourse?.title,
                          courseId: currentCourse?.id,
                          stepTitle: currentStep.name,
                          stepId: currentStep.id,
                        }}
                      />
                    </div>
                  )}
                  {currentStep.type === StepType.Text && (
                    <div
                      className={styles.textContainer}
                      dangerouslySetInnerHTML={{ __html: currentStep.content }}
                    />
                  )}
                </>
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={bannerImage}
                  alt={currentCourse?.title}
                  className={styles.bannerImage}
                />
              )}
            </div>
          )}
          <div className={styles.titleContainer}>
            {isLoading ? (
              <Skeleton variant="text" width="200px" />
            ) : (
              <Typography variant="h6">{currentCourse?.title}</Typography>
            )}
            {isLoading ? (
              <Skeleton variant="text" width="100px" />
            ) : (
              <Typography
                variant="body1"
                color="text.secondary"
                className={styles.subtitle}
              >
                {`${currentCourse?.total_steps} Steps`} <DotSeparator />
                {totalTime}
              </Typography>
            )}
          </div>
          <Tabs
            className={cx(styles.tabs, styles.superlightTabs)}
            value={tab}
            variant="scrollable"
            // scrollButtons="auto"
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
          >
            <Tab disabled className={cx(styles.tab, styles.disabledTab)} />
            {courseTabs.map((item) => (
              <Tab
                key={item.value}
                aria-selected={item.value === tab}
                label={item.label}
                tabIndex={0}
                iconPosition="start"
                value={item.value}
                className={cx(styles.tab, styles.tabMotion, {
                  [styles.selectedTab]: tab === item.value,
                })}
              />
            ))}
            <Tab disabled className={cx(styles.tab, styles.disabledTab)} />
          </Tabs>
          {tab === 0 && (
            <div className={styles.tabContainer}>
              <Typography variant="h6" color="text.secondary">
                Description
              </Typography>
              {isLoading ? (
                <Skeleton variant="text" width="400px" />
              ) : (
                <Typography
                  variant="body1"
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: currentCourse?.description || "",
                  }}
                />
              )}
              <div className={styles.categories}>
                {currentCourse?.categories.map((eachCategory) => (
                  <Tag
                    key={eachCategory.id}
                    label={eachCategory.name}
                    className={cx(styles.tag, styles.tagMotion)}
                    color={"info"}
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedCategory(eachCategory.id)}
                  />
                ))}
              </div>
              {selectedCategory && (
                <CategoryCoursesModal
                  categoryId={selectedCategory}
                  onClose={() => setSelectedCategory(undefined)}
                />
              )}
            </div>
          )}
          {tab === 1 && (
            <div>
              {sections.map((eachSection, idx) => {
                const sortedSteps = [...(eachSection.steps || [])].sort(
                  (firstStep, secondStep) => firstStep.order - secondStep.order
                );
                return (
                  <Accordion key={eachSection.id} defaultExpanded>
                    <AccordionSummary
                      expandIcon={
                        <ChevronIcon
                          dir="down"
                          className={styles.accordionIcon}
                        />
                      }
                      // variant="body1"
                      // color="text.secondary"
                      // className={styles.sectionText}
                    >
                      {`Section ${idx + 1} - ${eachSection.name}`}
                    </AccordionSummary>
                    <AccordionDetails>
                      {sortedSteps.map((eachStep, stepIdx) => (
                        <div
                          key={eachStep.id}
                          className={cx(styles.stepContainer, {
                            [styles.selectedStep]:
                              eachStep.id === currentStep?.id,
                          })}
                          role="presentation"
                          onClick={() => setCurrentStep(eachStep)}
                        >
                          <Typography
                            variant="body1"
                            className={styles.stepNumber}
                          >
                            {stepIdx + 1}
                          </Typography>
                          <div className={styles.stepContent}>
                            <Typography variant="body1">
                              {eachStep.name}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              color={"text.secondary"}
                              className={styles.durationText}
                            >
                              {eachStep.type === StepType.Video
                                ? getStepDuration(eachStep.length_in_min)
                                : ""}
                            </Typography>
                          </div>
                          {eachStep.step_is_seen ? (
                            <TickIcon
                              className={cx(styles.stepIcon, styles.checkIcon)}
                            />
                          ) : (
                            <>
                              {eachStep.type === StepType.Video && (
                                <PlayCircleIcon
                                  variant="outlined"
                                  className={cx(styles.stepIcon, {
                                    [styles.stepSeenIcon]:
                                      eachStep.step_is_seen,
                                  })}
                                />
                              )}
                              {eachStep.type === StepType.Text && (
                                <FeedIcon
                                  variant="outlined"
                                  className={cx(styles.stepIcon, {
                                    [styles.stepSeenIcon]:
                                      eachStep.step_is_seen,
                                  })}
                                />
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          )}
          {tab !== 1 && (
            <div className={styles.fixedActionContainer}>
              <Button
                onClick={goToFirstUnseenStep}
                className={styles.actionButton}
              >
                {isCourseStarted ? "Continue Learning" : "Start Learning"}
              </Button>
            </div>
          )}
        </div>
      </MobileModal>
      {!gworldAcademy && <LockedDialog />}
    </>
  );
};

export default MobileCoursePage;
